import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import FiltersMenu, { DropdownOption } from "../components/FiltersMenu";
import { CONCORE_EPD_FILTER_CATEGORIES } from "../consts";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";

const EPDFilter = () => {
  const { t } = useTranslation();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [categories, setCategories] = React.useState<DropdownOption[]>([]);

  React.useEffect(() => {
    epdSearchStore.setSelectedFilters([]);
    getFilterCategories();
  }, [epdSearchStore.isExternalSearch]);

  const getFilterCategories = async () => {
    if (epdSearchStore.isExternalSearch) {
      const categories = await epdSearchStore.fetch2050FilterCategories();
      const categoriesList = categories?.map((rec) => ({
        id: rec.id,
        name: rec.category_name,
        name_de: rec.category_name_de,
        value: rec.category_key,
        hasChildren: true,
      }));
      categoriesList && setCategories(categoriesList);
    } else {
      const filteredCategories = CONCORE_EPD_FILTER_CATEGORIES.filter(
        (rec) => rec.id !== "creator"
      );

      setCategories(
        epdSearchStore.isOrganisationalSearch
          ? filteredCategories
          : CONCORE_EPD_FILTER_CATEGORIES
      );
    }
  };

  const getFilterOptions = async (
    id: string,
    searchTerm: string,
    pageNumber: number,
    pageSize: number
  ) => {
    if (id === "creator") {
      // Pass static items
      return {
        count: 2,
        items: [
          {
            id: "current",
            name: sidebarMenuStore.currentOrganisation?.name,
            name_de: sidebarMenuStore.currentOrganisation?.name,
            hasChildren: false,
          },
          {
            id: "concular",
            name: "Circular LCA",
            name_de: "Circular LCA",
            hasChildren: false,
          },
        ],
      };
    }

    if (epdSearchStore.isExternalSearch) {
      const response = await epdSearchStore.fetch2050FilterChildren(
        String(id),
        searchTerm,
        pageNumber,
        pageSize
      );
      if (!response) return;
      const options =
        response.items?.map(
          (item) =>
            ({
              id: item.option_id,
              name: item.option_name,
              name_de: item.option_name_de,
              hasChildren: false,
              badge: item.badge,
            } as DropdownOption)
        ) ?? [];
      const updatedItems = {
        count: response.count,
        items: options,
      };
      return updatedItems;
    } else {
      const response = await epdSearchStore.fetchConcoreEPDFilterChildren(
        String(id),
        searchTerm,
        pageNumber,
        pageSize
      );
      if (!response) return;
      let options: DropdownOption[];
      if (id === "reference_unit") {
        options =
          response.items?.map((item) => {
            const currentItem = REFERENCE_UNITS.find((rec) => rec.id === item);
            return {
              id: item,
              name: currentItem?.name,
              name_de: currentItem?.name_de,
              hasChildren: false,
            } as DropdownOption;
          }) ?? [];
      } else if (id === "data_type") {
        options =
          response.items?.map((item) => {
            return {
              id: item,
              name: t("EPDEditor.types." + item),
              name_de: t("EPDEditor.types." + item),
              value: item,
            } as DropdownOption;
          }) ?? [];
      } else {
        options =
          response.items?.map(
            (item) =>
              ({
                id: item,
                name: item,
                name_de: item,
                hasChildren: false,
              } as DropdownOption)
          ) ?? [];
      }
      const updatedItems = {
        count: response.count,
        items: options,
      };
      return updatedItems;
    }
  };

  if (categories.length === 0) return;
  return (
    <div id="filter-wrapper" ref={wrapperRef} className="relative">
      <FiltersMenu
        wrapperID="filter-wrapper"
        options={categories}
        fetchChildren={getFilterOptions}
        pageSize={25}
        onSelectItem={(items) => epdSearchStore.setSelectedFilters(items)}
        checkedItems={epdSearchStore.selectedFilters}
        noResultText={t("productEditor.noFilterOptionText")}
        disabled={epdSearchStore.fetchingEPDsLoading}
        singleSelect={epdSearchStore.isExternalSearch}
      />
    </div>
  );
};

export default observer(EPDFilter);
